import { gql } from "@apollo/client";
import { PRODUCT_FILE_FIELDS } from "./productQueries";

export const UPDATE_PRODUCT_COLLECTIONS = gql`
  mutation UpdateProductCollections($id: Int!, $collections: [CollectionWhereUniqueInput!]!) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      collections: $collections
    ) {
      id
      collection {
        id
        collection_profiles {
          name
          language
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_TAGS = gql`
  mutation UpdateProductTags($id: Int!, $tags: [TagWhereUniqueInput!]!) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      tags: $tags
    ) {
      id
      tags {
        id
        tag_profiles {
          name
          language
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_CATEGORIES = gql`
  mutation UpdateProductCategories(
    $id: Int!
    $categories: [CategoryWhereUniqueInput!]!
    $primary_categoryId: Int
  ) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      categories: $categories
      primary_categoryId: $primary_categoryId
    ) {
      id
      primary_categoryId
      categories {
        id
        category_profiles {
          name
          language
        }
      }
    }
  }
`;

export const UPDATE_PRODUCT_IMAGES = gql`
  mutation UpdateProductImages($id: Int!, $images: [String!]!) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      images: $images
    ) {
      id
      advanced_product {
        id
        category_photo_id
        images {
          id
          src
          src_thumbnail
          src_md
          display_order
        }
      }
    }
  }
`;

export const CREATE_PRODUCT = gql`
  mutation CREATE_ADVANCED_PRODUCT(
    $advanced_product_profiles: [AdvancedProductProfileCustomInput!]!,
    $images: [String],
    $product_details: ProductDetailCreateInput,
    $visible: Boolean!,
    $featured: Boolean!,
    $manufacturer: Int!,
    $discount: DiscountUncheckedCreateInput,
    $dispatch_information: DispatchInformationUncheckedCreateInput,
    $styles: [StyleWhereUniqueInput],
    $collections: [CollectionWhereUniqueInput],
    $categories: [CategoryWhereUniqueInput],
    $tags: [TagWhereUniqueInput],
    $content_blocks: [ContentBlockWhereUniqueInput],
    $additional_notes: String,
    $advanced_product_skeleton: AdvancedProductSkeletonInput!,
    $advanced_product_fabric_category_prices: [AdvancedProductCategoryPriceInput!],
    $dimensions: DimensionCreateInput,
    $additional_component_groups: [AdditionalComponentGroupCustomInput],
    $sku: String,
    $ean_code: BigInt,
    $minimum_order_quantity: Int,
    $base_prices: [PriceForPriceListInput],
    $shipping_methods: [ShippingMethodWhereUniqueInput],
    $country_of_origin: String,
    $SVP_boost: Int,
    $fabricCombinations: [FabricCombinationCustomCreateInput]
  ) {
    createNewAdvancedProduct(
      advanced_product_profiles: $advanced_product_profiles
      images: $images
      product_details: $product_details
      visible: $visible
      featured: $featured
      manufacturer: $manufacturer
      discount: $discount
      dispatch_information: $dispatch_information
      styles: $styles
      collections: $collections
      categories: $categories
      tags: $tags
      content_blocks: $content_blocks
      advanced_product_skeleton: $advanced_product_skeleton
      additional_notes: $additional_notes
      dimensions: $dimensions
      advanced_product_fabric_category_prices: $advanced_product_fabric_category_prices
      additional_component_groups: $additional_component_groups
      sku: $sku
      ean_code: $ean_code
      minimum_order_quantity: $minimum_order_quantity
      base_prices: $base_prices
      shipping_methods: $shipping_methods
      country_of_origin: $country_of_origin
      SVP_boost: $SVP_boost
      fabricCombinations: $fabricCombinations
    ) {
      id
      __typename
    }
  }
`;

export const UPDATE_FURNITURE_TYPE = gql`
  mutation UpdateFurnitureType($id: Int!, $advanced_product_type: AdvancedProductType!) {
    updateExistingAdvancedProduct2(
      where: { id: $id }
      advanced_product_type: $advanced_product_type
    ) {
      id
      advanced_product {
        id
        advanced_product_type
      }
    }
  }
`;

export const SET_LINKED_PRODUCTS = gql`
  mutation SetLinkedProducts($productId: Int!, $linked_products: [LinkedProductInput!]!) {
    setLinkedProducts(productId: $productId, linked_products: $linked_products) {
      id
    }
  }
`;

export const SET_CATEGORY_PHOTO = gql`
  mutation SetCategoryPhoto($input: SetCategoryPhotoInput!) {
    setCategoryPhoto(input: $input)
  }
`

export const UPDATE_PRODUCT_CONTENT_BLOCKS = gql`
  mutation UpdateProductContentBlocks(
    $id: Int!
    $content_blocks: [ContentBlockWhereUniqueInput!]!
  ) {
    updateProductContainerContentBlocks(
      id: $id
      content_blocks: $content_blocks
    ) {
      id
    }
  }
`;

export const UPDATE_SINGLE_PRODUCT_IMAGES = gql`
  mutation UpdateSingleProductImages($id: Int!, $images: [String!]!) {
    updateSingleProductImages(id: $id, images: $images) {
      id
      single_product {
        id
        category_photo_id
        images { id src src_md src_thumbnail display_order }
      }
    }
  }
`

// Full-replace of the HAND-ATTACHED (erp_managed: false) product files only —
// scoped server-side, see updateProductFiles in furnisystems-backend
// src/types-modified/Products/productFileOperations.ts. Returns BOTH
// partitions (ERP + hand-attached) including suppressed rows, so the whole
// Files section can be re-rendered from this one response.
export const UPDATE_PRODUCT_FILES = gql`
  ${PRODUCT_FILE_FIELDS}
  mutation UpdateProductFiles($productContainerId: Int!, $files: [ProductFileInput!]!) {
    updateProductFiles(productContainerId: $productContainerId, files: $files) {
      ...ProductFileFields
    }
  }
`

// Converts an ERP-owned row into a hand-attached one. One-way — there is no
// un-claim, since handing a row back would make the next nightly sync free to
// delete it.
export const CLAIM_PRODUCT_FILE = gql`
  ${PRODUCT_FILE_FIELDS}
  mutation ClaimProductFile($id: Int!) {
    claimProductFile(id: $id) {
      ...ProductFileFields
    }
  }
`

// Hides a wrongly-attached row without deleting it (deleting an ERP row would
// let the next nightly sync recreate it, undoing the correction every night).
export const SET_PRODUCT_FILE_SUPPRESSED = gql`
  ${PRODUCT_FILE_FIELDS}
  mutation SetProductFileSuppressed($id: Int!, $suppressed: Boolean!) {
    setProductFileSuppressed(id: $id, suppressed: $suppressed) {
      ...ProductFileFields
    }
  }
`
